import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { deleteJob, getSoraPricing } from '../services/soraService';
import { useSoraJobs } from '../hooks/useSoraJobs';
import { useResetCountdown } from '../hooks/useResetCountdown';
import CreditStatusSummary from '../components/common/CreditStatusSummary';
import RecentJobs from '../components/dashboard/RecentJobs';
import UserActivityStats from '../components/dashboard/UserActivityStats';
import OnboardingTips from '../components/dashboard/OnboardingTips';
import NotificationsPanel from '../components/dashboard/NotificationsPanel';
import { getSubscriptionSummary, getFreeDownloadBadge } from '../utils/subscription';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from 'react-i18next';
import { downloadJobResult } from '../utils/downloadMedia';

const Dashboard = () => {
  const navigate = useNavigate();
  const { credits, userProfile, firebaseUser } = useAuth();
  const toast = useToast();
  const { t } = useTranslation(['dashboard', 'common']);

  // Utiliser RTDB pour les jobs (temps râ”œÂ®el, â”œÂ®vite les 401 API)
  const { 
    jobs, 
    loading: jobsLoading, 
    removeJob,
    forceRefresh: refreshJobs 
  } = useSoraJobs();
  
  const [pricing, setPricing] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Countdown pour le reset quotidien du tâ”œÂ®lâ”œÂ®chargement gratuit
  const { shortFormat: countdownText } = useResetCountdown();

  // Charger uniquement le pricing via API
  useEffect(() => {
    getSoraPricing()
      .then((priceValue) => {
        setPricing(typeof priceValue === 'number' ? priceValue : null);
      })
      .catch(() => {
        // Silently fail, use default
      });
  }, [t]);

  const subscriptionSummary = useMemo(() => getSubscriptionSummary(userProfile), [userProfile]);
  const freeDownloadBadge = useMemo(
    () => getFreeDownloadBadge(subscriptionSummary, countdownText),
    [subscriptionSummary, countdownText]
  );

  // Le prix upscale dépend du plan: 15 (free) / 10 (Muzo+), via /sora/pricing
  const upscaleCost = pricing ?? 15;

  // Notifications intelligentes (hook centralisâ”œÂ®)
  const notifications = useNotifications({
    credits,
    userProfile,
    planInfo: subscriptionSummary,
    jobs,
    upscaleCost,
    navigate,
  });

  const showOnboarding = jobs.length === 0 && !jobsLoading;

  // Handlers
  const handleRecharge = () => navigate('/credits');
  const handleManagePlan = () => navigate('/credits');
  const handleUpgrade = () => navigate('/credits');
  const handleStartUpscale = () => navigate('/upscale');

  const handleDownloadJob = async (job) => {
    if (!job.result_url) return;
    setDownloadingId(job.job_id);
    try {
      await downloadJobResult(job);
    } catch {
      // Fallback si fetch â”œÂ®choue (CORS)
      const link = document.createElement('a');
      link.href = job.result_url;
      link.download = `${job.sora_id || 'video'}.mp4`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setDownloadingId(null);
  };

  const handleCopyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('actions.copyLink'));
    } catch {
      toast.error(t('actions.copyError'));
    }
  };

  const handleDeleteJob = async (job) => {
    if (!job?.job_id) return;
    if (!window.confirm(t('actions.deleteConfirm'))) return;
    try {
      await deleteJob(job.job_id);
      removeJob(job.job_id); // Update local state via hook
      toast.success(t('actions.deleteSuccess'));
    } catch (err) {
      toast.error(err?.message || t('actions.deleteError'));
    }
  };

  const handleRetryJob = (job) => {
    if (!job?.source_url) return;
    navigate(`/upscale?url=${encodeURIComponent(job.source_url)}`);
  };

  const handleUpscaleFromUrl = (url) => {
    navigate(`/upscale?url=${encodeURIComponent(url)}`);
  };

  // Greeting basâ”œÂ® sur l'heure
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greetings.morning');
    if (hour < 18) return t('greetings.afternoon');
    return t('greetings.evening');
  }, [t]);

  const displayName = useMemo(() => {
    return userProfile?.profile?.display_name 
      || firebaseUser?.displayName 
      || userProfile?.email?.split('@')[0]
      || t('user.default', { ns: 'common' });
  }, [userProfile, firebaseUser, t]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 text-slate-900 dark:text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        
        {/* Notifications */}
        <NotificationsPanel notifications={notifications} />

        {/* Header Section */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
              {greeting}, {displayName}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('greetings.welcome')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleStartUpscale}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98]"
          >
            <FontAwesomeIcon icon="infinity" />
            {t('actions.newUpscale', { ns: 'common' })}
          </button>
        </header>

        {/* Main Grid: Credits + Quick Actions */}
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,360px)] lg:items-start">
          {/* Left: Quick Actions Card */}
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm overflow-hidden h-full flex flex-col">
            <div className="flex items-center gap-3 px-5 py-4 sm:px-6 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-white/10 dark:to-white/5 border-b border-slate-200/60 dark:border-white/10">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white shadow-sm">
                <FontAwesomeIcon icon="bolt" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900 dark:text-white">{t('actions.quickActions')}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('actions.accessTools')}</p>
              </div>
            </div>
            <div className="p-4 sm:p-6 flex-1">
              <div className="grid gap-3 sm:grid-cols-2 h-full">
                <button
                  onClick={handleStartUpscale}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-left transition-all hover:border-indigo-300 dark:hover:border-indigo-500/30 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                  <div className="h-10 w-10 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FontAwesomeIcon icon="infinity" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{t('cards.upscale.title')}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('cards.upscale.priceLabel', { count: upscaleCost })}</p>
                  </div>
                </button>

                <button
                  onClick={handleStartUpscale}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-left transition-all hover:border-emerald-300 dark:hover:border-emerald-500/30 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                >
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FontAwesomeIcon icon="wand-magic-sparkles" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{t('cards.watermark.title')}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {subscriptionSummary.planType === 'pro' ? t('cards.watermark.descPro') : t('cards.watermark.descFree')}
                    </p>
                  </div>
                </button>

                <button
                  onClick={handleRecharge}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-left transition-all hover:border-amber-300 dark:hover:border-amber-500/30 hover:bg-amber-50 dark:hover:bg-amber-500/10"
                >
                  <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FontAwesomeIcon icon="coins" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{t('cards.recharge.title')}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('cards.recharge.desc')}</p>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/upscale?tab=history')}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-left transition-all hover:border-slate-300 dark:hover:border-white/20 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FontAwesomeIcon icon="clock-rotate-left" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{t('cards.history.title')}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('cards.history.desc')}</p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right: Credits Card */}
          <CreditStatusSummary
            credits={credits ?? 0}
            planLabel={subscriptionSummary.planLabel}
            planType={subscriptionSummary.planType}
            subscriptionStatus={subscriptionSummary.subscriptionStatus}
            monthlyCredits={subscriptionSummary.monthlyCredits}
            monthlyPrice={subscriptionSummary.monthlyPrice}
            creditsUsedThisCycle={subscriptionSummary.creditsUsedThisCycle}
            nextBilling={subscriptionSummary.nextBilling}
            isUnlimited={subscriptionSummary.isUnlimited}
            onRecharge={handleRecharge}
            onManagePlan={handleManagePlan}
            onUpgrade={handleUpgrade}
            freeDownloadBadge={freeDownloadBadge}
            variant="dashboard"
          />
        </div>

        {/* Activity Stats - utilise useCreditHistory en interne */}
        <UserActivityStats loading={jobsLoading} />

        {/* Recent Jobs OR Onboarding */}
        {showOnboarding ? (
          <OnboardingTips onStart={handleStartUpscale} />
        ) : (
          <RecentJobs
            jobs={jobs}
            loading={jobsLoading}
            onDownload={handleDownloadJob}
            onCopy={handleCopyLink}
            onDelete={handleDeleteJob}
            onRetry={handleRetryJob}
            onUpscale={handleUpscaleFromUrl}
            onRefresh={refreshJobs}
            downloadingId={downloadingId}
            upscaleCost={upscaleCost}
            maxDisplay={6}
            showViewAll={true}
          />
        )}

        {/* Footer Hint */}
        {!showOnboarding && jobs.length > 0 && (
          <div className="text-center py-4">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              <FontAwesomeIcon icon="shield-halved" className="mr-1.5" />
              Tes vidéos sont traitées sur nos serveurs GPU sécurisés et supprimées après 24h.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;



























